import React, { useMemo } from 'react';
import { useAppointments, useOpsSummary } from '../services/api';
import { Role, useAuth } from '../state/AuthContext';
import useMockDataToast from '../hooks/useMockDataToast';

const rolePlaybooks: Record<
  Role,
  { title: string; headline: string; actions: { label: string; detail: string }[] }
> = {
  medical_director: {
    title: 'Leadership priorities',
    headline: 'Keep ICU capacity smooth and approve escalations.',
    actions: [
      { label: 'Review ICU queue', detail: '2 cases awaiting medical director sign-off.' },
      { label: 'Approve med orders', detail: '3 controlled-substance orders pending compliance.' },
      { label: 'Staffing pulse', detail: 'Confirm overnight surgery staffing plan.' },
    ],
  },
  surgery: {
    title: 'Surgery focus',
    headline: 'Prep teams for ortho and soft tissue blocks.',
    actions: [
      { label: 'Pre-op huddles', detail: 'Ensure 4 patients completed fasting checks.' },
      { label: 'Instrument audit', detail: 'Verify OR2 tray sterilization before 14:00 block.' },
      { label: 'Recovery turnover', detail: 'Coordinate with ICU for post-op handoff slots.' },
    ],
  },
  internal_med: {
    title: 'Internal medicine',
    headline: 'Stabilize complex cases and communicate consult plans.',
    actions: [
      { label: 'Revisit differential list', detail: '2 referrals waiting for attending updates.' },
      { label: 'Owner consult prep', detail: 'Prep imaging studies before noon callbacks.' },
      { label: 'Care coordination', detail: 'Align lab follow-ups with ICU and pharmacy.' },
    ],
  },
  customer_service: {
    title: 'Client services',
    headline: 'Keep check-ins under 4 minutes and escalate billing risks.',
    actions: [
      { label: 'Check-in queue', detail: '4 arrivals within the next 30 minutes.' },
      { label: 'Billing outreach', detail: 'Call clients with balances over $500 today.' },
      { label: 'Discharge packets', detail: 'Print instructions for 3 afternoon procedures.' },
    ],
  },
  billing: {
    title: 'Revenue operations',
    headline: 'Triage A/R, approve payment plans, and de-risk audits.',
    actions: [
      { label: 'High balance review', detail: '3 invoices require leadership approval.' },
      { label: 'Insurance queue', detail: 'Follow up on 2 stalled claims > 10 days.' },
      { label: 'Audit spotlight', detail: 'Verify narcotics logs align with invoices.' },
    ],
  },
};

const criticalAlerts = [
  { title: 'Pharmacy', detail: 'Ketamine supply < 15 doses' },
  { title: 'Lab', detail: 'Blood analyzer requires calibration' },
  { title: 'Recovery', detail: '2 ICU cages reserved until 19:00' },
];

const formatMetricValue = (id: string, value: number) => {
  if (id === 'revenue') {
    return `$${Math.round(value).toLocaleString()}`;
  }
  return value.toLocaleString();
};

const Dashboard: React.FC = () => {
  const { data: opsSummary, isFallback, fallbackMessage } = useOpsSummary();
  useMockDataToast(isFallback, 'Operations summary offline. Showing cached pulse.', fallbackMessage);
  const { data: appointments = [] } = useAppointments();
  const { role } = useAuth();
  const playbook = role ? rolePlaybooks[role] : rolePlaybooks.medical_director;

  const appointmentPreview = useMemo(() => appointments.slice(0, 5), [appointments]);

  const statusTotal =
    opsSummary.appointmentStatus.reduce((total, status) => total + status.count, 0) || 1;
  const revenueTotal = opsSummary.revenue.pending + opsSummary.revenue.collected;
  const revenueProgress = revenueTotal
    ? Math.round((opsSummary.revenue.collected / revenueTotal) * 100)
    : 0;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="section-title mb-4">Operational overview</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {opsSummary.metrics.map((metric) => (
            <div key={metric.id} className="glass-panel p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {metric.label}
              </p>
              <p className="mt-3 text-3xl font-semibold text-slate-900">
                {formatMetricValue(metric.id, metric.value)}
              </p>
              <p className="mt-1 text-xs text-slate-500">{metric.trend}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="glass-panel lg:col-span-2">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <p className="section-title mb-1">Today? appointments</p>
              <h3 className="text-lg font-semibold text-slate-900">Triage & procedures</h3>
            </div>
            <button
              type="button"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-brand-blue hover:text-brand-blue"
            >
              Export schedule
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] table-fixed divide-y divide-slate-100">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-6 py-3">Patient</th>
                  <th className="px-6 py-3">Owner</th>
                  <th className="px-6 py-3">Time</th>
                  <th className="px-6 py-3">Doctor</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white text-sm text-slate-700">
                {appointmentPreview.map((appt) => {
                  const time = new Date(appt.date).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  return (
                    <tr key={appt.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{appt.petName}</td>
                      <td className="px-6 py-4">{appt.owner ?? 'Owner TBD'}</td>
                      <td className="px-6 py-4 text-slate-500">{time}</td>
                      <td className="px-6 py-4">{appt.vetName}</td>
                      <td className="px-6 py-4">
                        <span className="pill bg-emerald-50 text-emerald-600">
                          {appt.status ?? 'Scheduled'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass-panel space-y-3 p-6">
            <div>
              <p className="section-title mb-1">Queue status</p>
              <h3 className="text-lg font-semibold text-slate-900">Appointment load</h3>
            </div>
            <ul className="space-y-2 text-sm">
              {opsSummary.appointmentStatus.map((status) => {
                const percentage = Math.round((status.count / statusTotal) * 100);
                return (
                  <li key={status.status}>
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                      <span>{status.status}</span>
                      <span>{status.count}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-indigo"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="glass-panel space-y-3 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="section-title mb-1">Financial pulse</p>
                <h3 className="text-lg font-semibold text-slate-900">Revenue snapshot</h3>
              </div>
              <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                {revenueProgress}% collected
              </span>
            </div>
            <p className="text-sm text-slate-500">
              ${Math.round(opsSummary.revenue.pending).toLocaleString()} pending 繚 $
              {Math.round(opsSummary.revenue.collected).toLocaleString()} collected this week
            </p>
            <div className="h-3 rounded-full bg-slate-100">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-brand-blue to-brand-sky"
                style={{ width: `${Math.min(revenueProgress, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="glass-panel space-y-4 p-6">
          <div>
            <p className="section-title mb-1">{playbook.title}</p>
            <h3 className="text-lg font-semibold text-slate-900">{playbook.headline}</h3>
          </div>
          <ul className="space-y-3 text-sm">
            {playbook.actions.map((action) => (
              <li
                key={action.label}
                className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
              >
                <p className="font-semibold text-slate-900">{action.label}</p>
                <p className="text-xs text-slate-500">{action.detail}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass-panel space-y-4 p-6">
          <div>
            <p className="section-title mb-1">Critical alerts</p>
            <h3 className="text-lg font-semibold text-slate-900">Clinical watchlist</h3>
          </div>
          <ul className="space-y-3">
            {criticalAlerts.map((alert) => (
              <li
                key={alert.title}
                className="rounded-2xl border border-amber-100 bg-amber-50/80 p-3 text-sm text-amber-900"
              >
                <p className="font-semibold">{alert.title}</p>
                <p className="text-xs text-amber-700">{alert.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;



